import { calculateTimelineText, isTop3 } from 'fitbeat.gym-core';
import { IUserLeaderboardViewModel } from 'fitbeat.models';
import React, { FunctionComponent, useRef, useState } from 'react';
import {
  ActivityIndicator, Platform, StyleSheet, Text, TouchableOpacity, View, ScrollView,
} from 'react-native';
import MedalStarIcon from '../../../assets/icons/medal-star.svg';
import { Icon } from '../../../components';
import Label from '../../../components/Label';
import { HorizontalScroller, horizontalScrollConstants } from '../../../SummaryDashboard/components/HorizontalScroller';
import { HorizontalScrollerCard } from '../../../SummaryDashboard/components/HorizontalScrollerCard';
import { leaderboardSubHeading } from '../../../SummaryDashboard/components/LeaderboardOverview';
import Theme from '../../../theme';
import { defaultScale } from '../../../utils';
import { scaleText } from '../../../utils/scaling';
import { isWeb } from '../../../utils/ScreenSizes';
import { rankColorMapping } from '../LeaderboardPositionIndicator';
import LeaderboardOverviewPosition from './LeaderboardOverviewPosition';
import LeaderboardSectionHeading from './LeaderboardSectionHeading';
import { HideScrollBarOnWeb } from '../../../components/HideScrollBarOnWeb';

interface IProps {
  navigation: any;
  isLoadingLeaderboards: boolean;
  leaderboards: IUserLeaderboardViewModel[];
  onLeaderboardPress(leaderboard: IUserLeaderboardViewModel): void;
  t(key: string): string;
}

const CompletedLeaderboardsSlider: FunctionComponent<IProps> = ({
  navigation,
  isLoadingLeaderboards,
  leaderboards,
  onLeaderboardPress,
  t,
}) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  if (isLoadingLeaderboards) {
    return (
      <ActivityIndicator
        size='large'
        animating
        color={Theme.PRIMARY}
        style={styles.loader}
      />
    );
  }
  if (!leaderboards || leaderboards.length === 0) {
    return <></>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <LeaderboardSectionHeading heading={t('tracking:completedLeaderboardsSliderHeading')} />
        <TouchableOpacity
          onPress={() => navigation.navigate('CompletedLeaderboards')}
          style={styles.seeAllHeading}
        >
          <Text style={styles.seeAllButton}>{t('tracking:seeAllButton')}</Text>
        </TouchableOpacity>
      </View>
      <HideScrollBarOnWeb>
        <HorizontalScroller
          ref={scrollViewRef}
          currentIndex={currentIndex}
          onIndexChange={setCurrentIndex}
          totalItems={leaderboards.length}
        >
          {leaderboards.map((leaderboard, index) => (
            <HorizontalScrollerCard
              key={`${leaderboard.leaderboardId}-${leaderboard.startDate}-${leaderboard.endDate}`}
              onPress={() => onLeaderboardPress(leaderboard)}
              style={{}}
              lastCard={index === leaderboards.length - 1}
              wideCard={leaderboards.length === 1}
            >
              <View style={styles.cardContainer}>
                <View style={styles.cardHeadingContainer}>
                  <Text style={styles.cardHeading}>{leaderboard.name}</Text>
                  <Icon
                    size={defaultScale(18)}
                    color={Theme.GREY_DARK_MEDIUM}
                    name='ios-arrow-forward'
                  />
                </View>
                <Text style={styles.cardSubHeading}>{leaderboardSubHeading(leaderboard, t)}</Text>
                <View style={styles.cardBodyContainer}>
                  <View style={styles.iconPositionContainer}>
                    <MedalStarIcon
                      color={
                        leaderboard?.position && isTop3(leaderboard.position)
                          ? rankColorMapping[leaderboard.position]
                          : Theme.GREY_MEDIUM
                      }
                      style={isWeb() ? { marginRight: defaultScale(12) } : styles.medalIcon}
                    />
                    <LeaderboardOverviewPosition leaderboard={leaderboard} t={t} />
                  </View>
                  <View style={styles.timelineTextContainer}>
                    {index === 0 && (
                      <Label
                        text={t('tracking:featured')}
                        alignment='flex-end'
                        containerStyle={{ marginVertical: defaultScale(5) }}
                      />
                    )}
                    <Text style={styles.timelineText}>
                      {calculateTimelineText(leaderboard.startDate, leaderboard.endDate, leaderboard.timezone)}
                    </Text>
                  </View>
                </View>
              </View>
            </HorizontalScrollerCard>
          ))}
        </HorizontalScroller>
      </HideScrollBarOnWeb>
    </View>
  );
};

const styles = StyleSheet.create({
  seeAllHeading: {
    marginTop: defaultScale(22),
    marginRight: defaultScale(6),
  },
  seeAllButton: {
    fontFamily: Theme.textStyle.heading2.fontFamily,
    color: Theme.PRIMARY,
    fontSize: scaleText(16),
    paddingRight: defaultScale(10),
  },
  cardContainer: {
    backgroundColor: Theme.WHITE,
    padding: defaultScale(15),
  },
  cardHeadingContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: defaultScale(Theme.textStyle.heading2.fontSize * 2 + 10),
  },
  headerContainer: {
    width: '100%',
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  cardHeading: {
    ...Theme.textStyle.heading2,
  },
  cardSubHeading: {
    ...Theme.textStyle.bodyCopyReg,
    marginTop: defaultScale(5),
    marginBottom: defaultScale(10),
  },
  cardBodyContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  medalIcon: {
    marginRight: defaultScale(12),
  },
  iconPositionContainer: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'flex-end',
  },
  timelineTextContainer: {
    justifyContent: 'flex-end',
  },
  timelineText: {
    ...Theme.textStyle.small,
  },
  loader: {
    height: defaultScale(300),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: defaultScale(20),
    alignSelf: 'center',
  },
  container: {
    flex: 1,
  },
});

export default CompletedLeaderboardsSlider;
